import React, { useEffect, useMemo, useState } from "react";
import { FaShoppingCart } from "react-icons/fa";
import { useCart } from "../context/CartContextCliente";

import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";

// ✅ Iconos redes
import { FaInstagram, FaFacebook, FaTiktok } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

export default function NavbarCliente({ empresa, onCarritoClick, style }) {
  const { cart } = useCart();

  const [landing, setLanding] = useState(null);
  const [showAbout, setShowAbout] = useState(false);
  const [loadingAbout, setLoadingAbout] = useState(false);

  useEffect(() => {
    const fetchLanding = async () => {
      try {
        if (!empresa?.email) return;
        setLoadingAbout(true);

        const refEmpresa = doc(db, "empresas", empresa.email);
        const snap = await getDoc(refEmpresa);

        if (snap.exists()) {
          const data = snap.data();
          setLanding(data?.landing || null);
        }
      } catch (e) {
        console.warn("Error leyendo landing:", e);
        setLanding(null);
      } finally {
        setLoadingAbout(false);
      }
    };

    fetchLanding();
  }, [empresa?.email]);

  // ✅ BLOQUEA scroll del body cuando el modal está abierto
  useEffect(() => {
    if (showAbout) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [showAbout]);

  // ✅ SOLO mostramos el botón si hay landing + habilitado + contenido real
  const seccionesActivas = useMemo(() => {
    const secs = landing?.secciones;
    if (!Array.isArray(secs)) return [];
    return secs.filter(
      (s) =>
        !!s?.enabled &&
        typeof s?.contenido === "string" &&
        s.contenido.trim().length > 0
    );
  }, [landing]);

  const mostrarBotonConocenos = useMemo(() => {
    if (!landing) return false;
    if (landing.mostrarSobreNosotros !== true) return false;
    if ((landing.titulo || "").trim().length === 0) return false;
    return seccionesActivas.length > 0;
  }, [landing, seccionesActivas]);

  // ✅ Normaliza redes: soporta string o {url, enabled}
  const redes = useMemo(() => {
    const r = landing?.redes || {};
    const items = [];

    const normalize = (key, label, raw) => {
      if (!raw) return null;

      if (typeof raw === "string") {
        const v = raw.trim();
        if (!v) return null;
        return { key, label, url: v };
      }

      if (typeof raw === "object") {
        const enabled = raw.enabled !== false;
        const url = (raw.url || "").toString().trim();
        if (!enabled || !url) return null;
        return { key, label, url };
      }

      return null;
    };

    const ig = normalize("instagram", "Instagram", r.instagram);
    const fb = normalize("facebook", "Facebook", r.facebook);
    const x = normalize("x", "X", r.x);
    const tt = normalize("tiktok", "TikTok", r.tiktok);

    [ig, fb, x, tt].forEach((v) => v && items.push(v));
    return items;
  }, [landing]);

  const toLink = (key, urlOrHandle) => {
    const v = (urlOrHandle || "").trim();
    if (!v) return "#";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;

    if (v.startsWith("@")) {
      const handle = v.replace("@", "");
      if (key === "instagram") return `https://instagram.com/${handle}`;
      if (key === "x") return `https://x.com/${handle}`;
      if (key === "tiktok") return `https://www.tiktok.com/@${handle}`;
      if (key === "facebook") return `https://www.facebook.com/${handle}`;
    }

    return v;
  };

  const iconByKey = (key) => {
    const common = { size: 18 };
    if (key === "instagram") return <FaInstagram {...common} />;
    if (key === "facebook") return <FaFacebook {...common} />;
    if (key === "x") return <FaXTwitter {...common} />;
    if (key === "tiktok") return <FaTiktok {...common} />;
    return null;
  };

  const closeModal = () => setShowAbout(false);

  // ==========================
  // ✅ CONTRASTE AUTOMÁTICO
  // ==========================
  const navbarBg = style?.backgroundColor || "red";

  const parseColorToRgb = (input) => {
    if (!input) return null;
    const c = String(input).trim();

    // #RGB / #RRGGBB
    if (c.startsWith("#")) {
      const hex = c.slice(1);
      if (hex.length === 3) {
        const r = parseInt(hex[0] + hex[0], 16);
        const g = parseInt(hex[1] + hex[1], 16);
        const b = parseInt(hex[2] + hex[2], 16);
        return { r, g, b };
      }
      if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return { r, g, b };
      }
      return null;
    }

    // rgb() / rgba()
    const m = c.match(/rgba?\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
    if (m) {
      return {
        r: Math.round(Number(m[1])),
        g: Math.round(Number(m[2])),
        b: Math.round(Number(m[3])),
      };
    }

    // Color name (no se puede calcular sin canvas); fallback
    return null;
  };

  const getContrastingText = (bg) => {
    const rgb = parseColorToRgb(bg);
    if (!rgb) return "#fff"; // fallback seguro

    // Relative luminance (sRGB)
    const srgb = [rgb.r, rgb.g, rgb.b].map((v) => {
      const x = v / 255;
      return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    });
    const L = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];

    // Umbral práctico
    return L > 0.55 ? "#111" : "#fff";
  };

  const brandColor = getContrastingText(navbarBg);
 

  return (
    <>
      <nav
        className="navbar shadow-sm px-3 fixed-top d-flex justify-content-between align-items-center"
        style={{
          backgroundColor: navbarBg,
          transition: "background-color 0.4s ease",
          height: "56px",
          flexWrap: "nowrap",
          overflow: "hidden",
          border: "2px solid #fff", // ✅ borde blanco 1px
          boxSizing: "border-box",
        }}
      >
        <div className="nn-navbar-shell d-flex justify-content-between align-items-center w-100">
        <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
          {empresa?.logoUrl && (
            <img
              src={empresa.logoUrl}
              alt="Logo"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                marginRight: "10px",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
          )}

          {/* ✅ TÍTULO: color automático según fondo */}
          <span
            className="navbar-brand mb-0 nn-brand"
            style={{
              fontWeight: 500,
              marginBottom: 0,
              maxWidth: "45vw",
              minWidth: 0,
              lineHeight: 1.05,
              fontSize: "0.95rem",
              color: brandColor, // ✅ negro si fondo claro, blanco si oscuro
            }}
            title={empresa?.nombre}
          >
            {empresa?.nombre}
          </span>
        </div>

        <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
          {mostrarBotonConocenos && (
            <button
              className="btn btn-warning btn-sm"
              type="button"
              onClick={() => setShowAbout(true)}
              style={{
                borderRadius: "999px",
                padding: "6px 10px",
                fontWeight: 900,
                letterSpacing: "0.4px",
                border: "none",
                whiteSpace: "nowrap",
                lineHeight: 1,
                height: "38px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                position: "relative",
                overflow: "hidden",
              }}
              title="Conocenos"
              aria-label="Conocenos"
            >
              <span style={{ fontSize: "1rem" }}>✨</span>
              <span className="d-none d-sm-inline" style={{ fontSize: "0.82rem" }}>
                CONOCENOS
              </span>

              <span
                style={{
                  position: "absolute",
                  top: "-30%",
                  left: "-50%",
                  width: "60%",
                  height: "170%",
                  background: "rgba(255,255,255,0.35)",
                  transform: "skewX(-20deg)",
                  animation: "nnShine 2.2s infinite",
                }}
              />
            </button>
          )}

          <button
            className="btn btn-light position-relative"
            onClick={onCarritoClick}
            style={{
              border: "none",
              height: "38px",
              width: "42px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            type="button"
            aria-label="Carrito"
          >
           <FaShoppingCart size={20} color="#0d6efd" />
            {cart.length > 0 && (
              <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                {cart.length}
              </span>
            )}
          </button>
        </div>
        </div>
      </nav>

      {/* ✅ MODAL CONOCENOS (SCROLLEABLE REAL) */}
      {showAbout && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            background:
              "radial-gradient(circle at 20% 10%, rgba(255,193,7,0.22), rgba(0,0,0,0.72) 45%, rgba(0,0,0,0.85))",
            zIndex: 9999,
            padding: "14px",
            backdropFilter: "blur(6px)",
          }}
          onClick={closeModal}
        >
          <div
            className="bg-white"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "780px",
              height: "90vh",
              borderRadius: "22px",
              overflow: "hidden",
              boxShadow: "0 18px 60px rgba(0,0,0,0.5)",
              border: "1px solid rgba(255,255,255,0.25)",
              display: "flex",
              flexDirection: "column",
              animation: "nnPop 160ms ease-out",
            }}
          >
            {/* HEADER FIJO */}
            <div
              style={{
                flex: "0 0 auto",
                background:
                  "linear-gradient(135deg, rgba(13,110,253,0.10), rgba(255,193,7,0.15))",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                padding: "12px 14px",
              }}
            >
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={closeModal}
                  style={{ borderRadius: "12px", fontWeight: 700 }}
                >
                  Cerrar
                </button>
              </div>

              <div className="d-flex flex-column align-items-center text-center mt-2">
                {empresa?.logoUrl ? (
                  <img
                    src={empresa.logoUrl}
                    alt="Logo"
                    style={{
                      width: "78px",
                      height: "78px",
                      borderRadius: "18px",
                      objectFit: "cover",
                      background: "#f1f3f5",
                      boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                      border: "3px solid rgba(255,255,255,0.9)",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "78px",
                      height: "78px",
                      borderRadius: "18px",
                      background: "#f1f3f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 900,
                    }}
                  >
                    LOGO
                  </div>
                )}

                <div style={{ marginTop: "10px", fontWeight: 900, fontSize: "1.25rem" }}>
                  {landing?.titulo || empresa?.nombre || "Conocenos"}
                </div>

                {(landing?.subtitulo || "").trim() ? (
                  <div className="text-muted" style={{ marginTop: "6px", fontSize: "0.95rem" }}>
                    {landing.subtitulo}
                  </div>
                ) : null}
              </div>
            </div>

            {/* BODY SCROLL */}
            <div
              style={{
                flex: "1 1 auto",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
                padding: "14px",
                background: "#fff",
              }}
            >
              {loadingAbout ? (
                <div className="d-flex align-items-center gap-2">
                  <div className="spinner-border text-primary" role="status" />
                  <span>Cargando info...</span>
                </div>
              ) : seccionesActivas.length === 0 ? (
                <div className="text-muted">Todavía no hay secciones activas para mostrar.</div>
              ) : (
                <>
                  {seccionesActivas.map((s) => (
                    <div
                      key={s.key}
                      className="mb-3"
                      style={{
                        borderRadius: "16px",
                        padding: "14px",
                        background:
                          "linear-gradient(135deg, rgba(13,110,253,0.06), rgba(255,193,7,0.08))",
                        border: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <div style={{ fontWeight: 900, marginBottom: "6px", fontSize: "1.05rem" }}>
                        {s.titulo}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                        {typeof s.contenido === "string" ? s.contenido : ""}
                      </div>
                    </div>
                  ))}

                  {/* ✅ REDES AL FINAL */}
                  {redes.length > 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: "1px dashed rgba(0,0,0,0.18)" }}>
                      <div className="fw-bold text-center mb-2">Seguinos en redes</div>

                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        {redes.map((r) => {
                          const href = toLink(r.key, r.url);
                          return (
                            <a
                              key={r.key}
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                                padding: "9px 12px",
                                borderRadius: "999px",
                                textDecoration: "none",
                                fontWeight: 800,
                                border: "1px solid rgba(0,0,0,0.08)",
                                background: "rgba(255,255,255,0.85)",
                                boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
                                color: "#111",
                              }}
                            >
                              <span
                                style={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: 999,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  background:
                                    r.key === "instagram"
                                      ? "linear-gradient(135deg,#f58529,#dd2a7b,#8134af,#515bd4)"
                                      : r.key === "facebook"
                                      ? "linear-gradient(135deg,#1877F2,#0e5ad1)"
                                      : r.key === "x"
                                      ? "linear-gradient(135deg,#111,#444)"
                                      : "linear-gradient(135deg,#25F4EE,#FE2C55)",
                                  color: "#fff",
                                  boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                                }}
                              >
                                {iconByKey(r.key)}
                              </span>
                              <span style={{ fontSize: "0.92rem" }}>{r.label}</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <style>
            {`
              /* ✅ 2 líneas en mobile (clamp), 1 línea en desktop */
              .nn-brand{
                display: -webkit-box;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
                -webkit-line-clamp: 2; /* mobile: 2 líneas */
                white-space: normal;   /* permite saltar de línea */
              }
              @media (min-width: 768px){
                .nn-brand{
                  -webkit-line-clamp: 1;
                  white-space: nowrap;
                }
              }

              @keyframes nnPop {
                from { transform: translateY(8px) scale(0.985); opacity: 0.5; }
                to { transform: translateY(0) scale(1); opacity: 1; }
              }
              @keyframes nnShine {
                0% { transform: translateX(-140%) skewX(-20deg); opacity: 0; }
                25% { opacity: 1; }
                60% { opacity: 1; }
                100% { transform: translateX(320%) skewX(-20deg); opacity: 0; }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
}
